// Floating ES/EN switch. Self-contained: translates the rendered DOM on the fly
// using the dictionary in lib/i18n.js. Delete this file + lib/i18n.js and the
// <LanguageToggle /> line in App.js to remove the feature entirely.
import React, { useEffect, useState } from 'react';
import { Languages } from 'lucide-react';
import { translate } from '../lib/i18n';

const KEY = 'd7-lang';
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'SVG', 'PATH', 'CODE']);
const ATTRS = ['placeholder', 'aria-label', 'title'];

const textNodes = new Map(); // textNode -> { orig, out }
const attrNodes = new Map(); // element -> { attr: { orig, out } }

const skip = (node) => {
  let el = node.parentElement;
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.dataset && el.dataset.noTranslate !== undefined) return true;
    el = el.parentElement;
  }
  return false;
};

const applyTo = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const pending = [];
  let n = walker.nextNode();
  while (n) { pending.push(n); n = walker.nextNode(); }
  if (root.nodeType === Node.TEXT_NODE) pending.push(root);
  for (const node of pending) {
    const cur = node.nodeValue;
    if (!cur || !cur.trim() || skip(node)) continue;
    const rec = textNodes.get(node);
    if (rec && rec.out === cur) continue;
    const out = translate(cur);
    if (out) { textNodes.set(node, { orig: cur, out }); node.nodeValue = out; }
  }
  const els = root.nodeType === Node.ELEMENT_NODE ? [root, ...root.querySelectorAll('*')] : [...(root.querySelectorAll ? root.querySelectorAll('*') : [])];
  for (const el of els) {
    for (const attr of ATTRS) {
      const cur = el.getAttribute && el.getAttribute(attr);
      if (!cur) continue;
      const store = attrNodes.get(el) || {};
      if (store[attr] && store[attr].out === cur) continue;
      const out = translate(cur);
      if (out) {
        store[attr] = { orig: cur, out };
        attrNodes.set(el, store);
        el.setAttribute(attr, out);
      }
    }
  }
};

const restoreAll = () => {
  for (const [node, rec] of textNodes) if (node.nodeValue === rec.out) node.nodeValue = rec.orig;
  textNodes.clear();
  for (const [el, store] of attrNodes) {
    for (const attr of Object.keys(store)) {
      if (el.getAttribute(attr) === store[attr].out) el.setAttribute(attr, store[attr].orig);
    }
  }
  attrNodes.clear();
};

const LanguageToggle = () => {
  const [lang, setLang] = useState(() => localStorage.getItem(KEY) || 'es');

  useEffect(() => {
    const sync = () => setLang(localStorage.getItem(KEY) || 'es');
    window.addEventListener('d7-lang', sync);
    return () => window.removeEventListener('d7-lang', sync);
  }, []);

  useEffect(() => {
    localStorage.setItem(KEY, lang);
    document.documentElement.lang = lang;
    if (lang !== 'en') { restoreAll(); return; }

    let observer;
    let frame = 0;
    const run = (root) => {
      if (observer) observer.disconnect();
      try { applyTo(root || document.body); } finally { if (observer) observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS }); }
    };
    observer = new MutationObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; run(); });
    });
    run();
    return () => { if (frame) cancelAnimationFrame(frame); observer.disconnect(); };
  }, [lang]);

  return (
    <div data-no-translate className="hidden" data-testid="translation-engine" />
  );
};

export default LanguageToggle;

// Visible ES/EN selector - used only on the landing page. The choice is stored
// in localStorage and applies to every screen of the 4 apps.
export const LanguageSwitch = ({ className = '' }) => {
  const [lang, setLang] = useState(() => localStorage.getItem(KEY) || 'es');
  const pick = (l) => {
    localStorage.setItem(KEY, l);
    setLang(l);
    window.dispatchEvent(new Event('d7-lang'));
  };
  return (
    <div data-no-translate className={`inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 shadow-sm p-1 ${className}`}>
      <Languages size={15} className="text-gray-400 ml-1.5" />
      {[['es', 'Español'], ['en', 'English']].map(([l, name]) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => pick(l)}
          title={name}
          className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide press ${lang === l ? 'bg-[#EE3B36] text-white shadow-sm' : 'text-gray-500 hover:text-[#1E2130]'}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};
